import { createClient } from '@supabase/supabase-js';

// Get environment variables with fallbacks
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const rawBodyCorporateId = process.env.REACT_APP_BODY_CORPORATE_ID || '';
const placeholderBodyCorporateId = '00000000-0000-0000-0000-000000000000';
export const DEFAULT_BODY_CORPORATE_ID = rawBodyCorporateId && rawBodyCorporateId !== placeholderBodyCorporateId
  ? rawBodyCorporateId
  : '';

export const hasConfiguredBodyCorporate = () => Boolean(DEFAULT_BODY_CORPORATE_ID);

// Log warning if variables are missing (but don't crash)
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('⚠️ Supabase environment variables are missing!');
  console.warn('Please create a .env file with:');
  console.warn('REACT_APP_SUPABASE_URL=your_supabase_url');
  console.warn('REACT_APP_SUPABASE_ANON_KEY=your_supabase_anon_key');
}

// Create the Supabase client with error handling
// Only create client if we have the required variables
let supabaseClient: any = null;

if (supabaseUrl && supabaseAnonKey) {
  try {
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });
    console.log('✅ Supabase client initialized successfully');
  } catch (error) {
    console.error('❌ Failed to initialize Supabase client:', error);
  }
} else {
  console.warn('⚠️ Supabase client not initialized - missing environment variables');
}

// Export the client (or a mock client for development)
export const supabase = supabaseClient || {
  // Mock client for development when variables are missing
  auth: {
    getUser: async () => ({ data: { user: null }, error: new Error('Supabase not configured') }),
    getSession: async () => ({ data: { session: null }, error: new Error('Supabase not configured') }),
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => undefined } } }),
    signInWithPassword: async () => ({ data: null, error: new Error('Supabase not configured') }),
    signUp: async () => ({ data: { session: null, user: null }, error: new Error('Supabase not configured') }),
    resetPasswordForEmail: async () => ({ data: null, error: new Error('Supabase not configured') }),
    updateUser: async () => ({ data: { user: null }, error: new Error('Supabase not configured') }),
    signOut: async () => ({ error: null }),
  },
  from: () => ({
    select: () => ({
      eq: () => ({
        single: async () => ({ data: null, error: new Error('Supabase not configured') }),
        order: () => ({
          then: (cb: any) => cb({ data: [], error: null })
        })
      }),
      order: () => ({
        then: (cb: any) => cb({ data: [], error: null })
      })
    }),
    insert: () => ({
      select: () => ({
        single: async () => ({ data: null, error: new Error('Supabase not configured') })
      })
    }),
    update: () => ({
      eq: () => ({
        then: (cb: any) => cb({ data: null, error: null })
      })
    }),
    delete: () => ({
      eq: () => ({
        then: (cb: any) => cb({ data: null, error: null })
      })
    })
  }),
  rpc: () => ({
    then: (cb: any) => cb({ data: 0, error: null })
  })
};

// ============================================
// HELPER FUNCTIONS
// ============================================

export const isSupabaseConfigured = (): boolean => {
  return !!supabaseUrl && !!supabaseAnonKey;
};

export const getCurrentBodyCorporateId = async (): Promise<string | null> => {
  try {
    const profile = await getCurrentProfileSafe();
    if (profile?.body_corporate_id) {
      return profile.body_corporate_id;
    }
  } catch (error) {
    console.warn('Unable to resolve a profile body corporate id:', error);
  }

  // This is a local-development fallback only. The signed-in profile is the
  // source of truth so the browser and protected Edge Functions use the same
  // body-corporate tenant.
  return DEFAULT_BODY_CORPORATE_ID || null;
};

export const getCurrentUser = async () => {
  if (!isSupabaseConfigured() || !supabaseClient) {
    console.warn('Supabase not configured');
    return null;
  }
  try {
    const { data: { user }, error } = await supabaseClient.auth.getUser();
    if (error) {
      console.error('Error getting user:', error);
      return null;
    }
    return user;
  } catch (error) {
    console.error('Error getting user:', error);
    return null;
  }
};

export const getCurrentUserId = async (): Promise<string | null> => {
  const user = await getCurrentUser();
  return user?.id || null;
};

/**
 * Get the current user's profile with email included
 * This will also create a profile if one doesn't exist
 */
export const getCurrentProfileSafe = async () => {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('No user logged in');
      return null;
    }

    const { data, error } = await supabaseClient
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      console.warn('Profile error:', error.message);
      
      // If profile doesn't exist, try to create it
      if (error.code === 'PGRST116') {
        console.log('Profile not found, creating default profile...');
        
        const { data: newProfile, error: insertError } = await supabaseClient
          .from('profiles')
          .insert({
            id: user.id,
            full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
            email: user.email || '',
            body_corporate_id: null,
            role: 'staff',
            is_active: true,
          })
          .select()
          .single();

        if (insertError) {
          console.error('Error creating profile:', insertError);
          return null;
        }

        console.log('✅ Profile created successfully');
        return newProfile;
      }

      return null;
    }

    // Add email from auth user if not in profile
    if (data && !data.email && user.email) {
      await supabaseClient
        .from('profiles')
        .update({ email: user.email })
        .eq('id', user.id);
      data.email = user.email;
    }

    return data;
  } catch (error) {
    console.error('Error in getCurrentProfileSafe:', error);
    return null;
  }
};

/**
 * Get the current user's profile (legacy function - use getCurrentProfileSafe instead)
 */
export const getCurrentProfile = async () => {
  const user = await getCurrentUser();
  if (!user) return null;

  try {
    const { data, error } = await supabaseClient
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Error getting profile:', error);
      return null;
    }
    return data;
  } catch (error) {
    console.error('Error getting profile:', error);
    return null;
  }
};

/**
 * Update the current user's profile
 */
export const updateProfile = async (updates: {
  full_name?: string;
  email?: string;
  role?: 'administrator' | 'staff' | 'viewer';
  body_corporate_id?: string;
}) => {
  const user = await getCurrentUser();
  if (!user) {
    throw new Error('You must be logged in to update your profile');
  }

  try {
    // Update profile in database
    const { data, error } = await supabaseClient
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;

    // If full_name is being updated, also update auth metadata
    if (updates.full_name) {
      await supabaseClient.auth.updateUser({
        data: {
          full_name: updates.full_name,
        },
      });
    }

    return data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

/**
 * Link a user to a body corporate
 */
export const linkToBodyCorporate = async (bodyCorporateId: string) => {
  return updateProfile({ body_corporate_id: bodyCorporateId });
};

/**
 * Check if the current user has a specific role
 */
export const hasRole = async (role: 'administrator' | 'staff' | 'viewer'): Promise<boolean> => {
  const profile = await getCurrentProfileSafe();
  if (!profile) return false;
  return profile.role === role;
};

/**
 * Check if the current user is an administrator
 */
export const isAdministrator = async (): Promise<boolean> => {
  return hasRole('administrator');
};

/**
 * Check if the current user is staff or higher
 */
export const isStaff = async (): Promise<boolean> => {
  const profile = await getCurrentProfileSafe();
  if (!profile) return false;
  return profile.role === 'administrator' || profile.role === 'staff';
};

// ============================================
// TYPES
// ============================================

export type BodyCorporate = {
  id: string;
  name: string;
  complex_name?: string;
  physical_address?: string;
  postal_address?: string;
  telephone?: string;
  email?: string;
  registration_number?: string;
  currency_code: string;
  currency_symbol: string;
  logo_path?: string;
  primary_color?: string;
  style_preset?: string;
  created_at: string;
  updated_at: string;
};

export type Flat = {
  id: string;
  body_corporate_id: string;
  flat_number: string;
  physical_address?: string;
  participation_quota?: number | null;
  status: 'occupied' | 'vacant' | 'inactive';
  created_at: string;
  updated_at: string;
};

export type Owner = {
  id: string;
  body_corporate_id: string;
  full_name: string;
  email?: string;
  telephone?: string;
  postal_address?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type Account = {
  id: string;
  body_corporate_id: string;
  flat_id: string;
  account_number: string;
  status: 'active' | 'closed' | 'archived';
  opened_on: string;
  created_at: string;
  updated_at: string;
};

export type AccountOwner = {
  id: string;
  account_id: string;
  owner_id: string;
  start_date: string;
  end_date?: string;
  is_primary: boolean;
  created_at: string;
};

export type Transaction = {
  id: string;
  body_corporate_id: string;
  account_id: string;
  transaction_date: string;
  posted_at: string;
  transaction_type: 'opening_balance' | 'monthly_levy' | 'quarterly_levy' | 'special_levy' | 'maintenance_levy' | 'payment' | 'penalty' | 'interest' | 'other_charge' | 'administrative_charge' | 'repair_charge' | 'miscellaneous_charge' | 'credit_adjustment' | 'reversal';
  description: string;
  reference_number?: string;
  levy_period?: string;
  due_date?: string;
  debit_amount: number;
  credit_amount: number;
  status: 'posted' | 'reversed' | 'cancelled' | 'draft';
  parent_transaction_id?: string;
  reversal_of_transaction_id?: string;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
};

export type Payment = {
  id: string;
  transaction_id: string;
  payment_method: 'bank_transfer' | 'cash' | 'cheque' | 'other';
  payment_reference?: string;
  received_date: string;
  notes?: string;
  created_at: string;
};

export type LevyRule = {
  id: string;
  body_corporate_id: string;
  name: string;
  levy_type: 'monthly' | 'quarterly' | 'special' | 'maintenance';
  default_amount: number;
  due_day: number;
  is_active: boolean;
  effective_from: string;
  effective_to?: string;
  created_at: string;
  updated_at: string;
};

export type PenaltyRule = {
  id: string;
  body_corporate_id: string;
  name: string;
  penalty_type: 'fixed' | 'percentage';
  amount_or_rate: number;
  grace_days: number;
  apply_once: boolean;
  is_active: boolean;
  effective_from: string;
  effective_to?: string;
  created_at: string;
  updated_at: string;
};

export type BankAccount = {
  id: string;
  body_corporate_id: string;
  bank_name: string;
  account_name: string;
  account_number: string;
  branch_code?: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
};

export type StatementSetting = {
  id: string;
  body_corporate_id: string;
  template_name: string;
  layout_style: 'professional' | 'compact';
  statement_title: string;
  intro_text?: string;
  summary_heading?: string;
  transactions_heading?: string;
  payment_heading?: string;
  notice_text?: string;
  footer_text?: string;
  payment_instructions?: string;
  terms_text?: string;
  created_at: string;
  updated_at: string;
};

export type Profile = {
  id: string;
  full_name: string;
  email?: string;
  body_corporate_id?: string;
  role: 'administrator' | 'staff' | 'viewer';
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type AuditLog = {
  id: string;
  body_corporate_id: string;
  user_id?: string;
  action: string;
  entity_type: string;
  entity_id: string;
  old_data?: any;
  new_data?: any;
  created_at: string;
};

export type EmailLog = {
  id: string;
  account_id: string;
  sent_to: string;
  subject: string;
  message?: string;
  sent_at: string;
  status: 'sent' | 'failed';
  error_message?: string;
  created_at: string;
};

export type PaymentAllocation = {
  id: string;
  body_corporate_id: string;
  payment_transaction_id: string;
  levy_transaction_id: string;
  allocated_amount: number;
  created_by?: string;
  created_at: string;
};
